/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

/** Whether an error is the server saying the session is over. */
export function isSessionEnded(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

export interface QueryClientOptions {
  /**
   * What to do when a request finds the session over: send the person to
   * sign in. Given by the application, which owns the router; left out, a 401
   * is only an error like any other.
   */
  onSessionEnded?: () => void | Promise<void>;
}

/**
 * The server-state client.
 *
 * Server state lives here and only here. Never copy a query result into a
 * Zustand store -- derive it with a selector instead (FE-2).
 *
 * A 401 from any query or mutation means the session ended under the page --
 * it timed out on the server, or was signed out elsewhere. Every screen used to
 * show that as its own failure, telling somebody to check that the API was
 * running when what they needed was to sign in again. It is answered once,
 * here: the cache is cleared, because it holds the last session's data, and
 * the person is sent to sign in.
 */
export function createQueryClient(options: QueryClientOptions = {}): QueryClient {
  const { onSessionEnded } = options;
  // Several queries on one screen fail together when a session ends; the
  // person is sent to sign in once, not once for each.
  let ending = false;
  const onError = (error: unknown) => {
    if (!onSessionEnded || !isSessionEnded(error) || ending) return;
    ending = true;
    // A turn later, so the cache is not cleared from inside its own callback
    // while the query that failed is still being settled. Held until the
    // navigation is done: the screen being left can ask again while it goes,
    // and those answers are the same 401.
    void Promise.resolve()
      .then(async () => {
        client.clear();
        await onSessionEnded();
      })
      .catch(() => {
        // Navigation that failed leaves the person where they are; the next
        // request that meets the ended session tries again.
      })
      .finally(() => {
        ending = false;
      });
  };

  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // A client error does not fix itself. A 401 means the session ended,
        // a 403 that the answer will not change, a 404 that it is not there --
        // retrying only delays what the person actually needs to see. Server
        // errors and network failures are worth another go.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            return false;
          }
          // A response that does not match the contract will not match it on
          // the second ask either. Somebody has to restart or rebuild.
          if (error instanceof ContractError) return false;
          // Retries add latency and flakiness to tests without proving
          // anything the retry predicate above does not already state.
          if (import.meta.env.MODE === 'test') return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: 0 },
    },
  });
  return client;
}

/** An error carrying the server's RFC 9457 problem detail. */
/** One field a request was refused over: where, the reason's code, and its words. */
export interface FieldProblem {
  /** Dotted, from the body: `counterparty_name`, `values.start_date`. */
  field: string;
  reason: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problemType: string;
  readonly correlationId: string;
  /**
   * The server's own sentence about what went wrong.
   *
   * Workflowd over column names and codes, never over values (`SEC-4`), so it
   * is safe to put in front of a person -- and it is usually the only part that
   * tells them what to change.
   */
  readonly detail: string;
  /**
   * The server's machine-readable reason, where it gives one --
   * `reminder.too_soon`, say -- so a screen can choose its own words for it.
   * Empty when the problem carries none.
   */
  readonly code: string;
  /**
   * When asking again will be allowed, for a refusal that says so (a 429
   * with `retry_after`). An ISO moment, or null.
   */
  readonly retryAfter: string | null;
  /**
   * Each field the server refused, and why, on a 422 that names them. The
   * message is the server's words for the rule, never the value typed; empty
   * where it has none.
   */
  readonly fields: readonly FieldProblem[];
  /** The questions an unfinished-agreement refusal is about, by key. */
  readonly missing: readonly string[];

  constructor(args: {
    status: number;
    title: string;
    problemType: string;
    correlationId: string;
    detail?: string;
    code?: string | undefined;
    retryAfter?: string | null | undefined;
    fields?: readonly FieldProblem[] | undefined;
    missing?: readonly string[] | undefined;
  }) {
    super(args.title);
    this.name = 'ApiError';
    this.status = args.status;
    this.problemType = args.problemType;
    this.correlationId = args.correlationId;
    this.detail = args.detail ?? '';
    this.code = args.code ?? '';
    this.retryAfter = args.retryAfter ?? null;
    this.fields = args.fields ?? [];
    this.missing = args.missing ?? [];
  }
}

/**
 * The server answered, and the answer was not the shape we asked for.
 *
 * Separate from `ApiError` because it is not an outage and not a refusal: in
 * development it almost always means the API is running older code than the
 * page — the field a screen needs was added after the server started. Saying
 * "check that the API is running" sends somebody to look at a process that is
 * running perfectly.
 */
export class ContractError extends Error {
  readonly path: string;

  constructor(path: string, cause: unknown) {
    super(`The response from ${path} did not match the contract`);
    this.name = 'ContractError';
    this.path = path;
    this.cause = cause;
  }
}
